import {
  createRootRoute,
  HeadContent,
  Outlet,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
// import { AuthCheck } from "../components/AuthCheck";
// import { OnboardingAuthCheck } from "../components/OnboardingAuthCheck";
import { Toaster } from "@/components/ui/sonner";
import { TourProvider } from "@/components/NextStepProvider";
import { AssetPreloader } from "@/components/AssetPreloader";
// The horizontal slide for the rest of the app is wired in
// RouterProvider.tsx via defaultViewTransition.types. The pixel cover
// overlay below stays for navigations to/from /world only.
import {
  pixelTransitionDefaults,
  isCloudRoute,
  isNavbarRoute,
} from "@/lib/configs/page-transition";
import { PixelPageTransition } from "@/components/PixelPageTransition";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { GlobalReferralCapture } from "@/components/GlobalReferralCapture";
import { RefCheck } from "@/components/RefCheck";
import { FirstLoginRedirect } from "@/components/FirstLoginRedirect";
import { MusicProvider } from "@/lib/providers/MusicProvider";
import NotifToastTrigger from "@/components/NotifToastTrigger";
import PwaUpdateToast from "@/components/PwaUpdateToast";
import { PosthogLoginIdentifier } from "@/components/PosthogLoginIdentifier";
import { MoonPayRecoveryToastNotifier } from "@/components/MoonPayCrashBoundary";
import { AuthCheck } from "@/components/AuthCheck";
import { PowerUserDialog } from "@/components/PowerUserDialog";
// import OneSignalInit from "@/components/OneSignalInit";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { TeleportWidget } from "@/components/TeleportWidget";
import { MissionWidget } from "@/components/MissionWidget";
import { WelcomePackDialog } from "@/components/WelcomePackDialog";
import { ReplayTutorialDialog } from "@/components/ReplayTutorialDialog";
import { SnowfallOverlay } from "@/components/SnowfallOverlay";
import { Captcha } from "@privy-io/react-auth";
import { AuthCheckForWMA } from "@/components/AuthCheckForWMA";
import { MiniKit } from "@worldcoin/minikit-js";
import { PixelErrorFallback } from "@/components/PixelErrorFallback";
import { PixelNotFound } from "@/components/PixelNotFound";
import { MaintenanceOverlay } from "@/components/MaintenanceOverlay";
import { GeoBlockOverlay } from "@/components/GeoBlockOverlay";

const ENV = import.meta.env.VITE_ENVIRONMENT as
  | "development"
  | "production"
  | "test"
  | undefined;

if (ENV === "development") {
  import("eruda").then((eruda) => eruda.default.init());
}


const RootLayout = () => {
  const location = useLocation();
  const router = useRouter();

  const isDesktop = useMediaQuery("(min-width: 768px)");
  const allowSnowfall = false;

  // Pixel cover-overlay state. Used only for navigations to/from
  // /world (everything else uses the slide via defaultViewTransition).
  const [pixelTransitionToken, setPixelTransitionToken] = useState<
    number | null
  >(null);
  const closingResolveRef = useRef<(() => void) | null>(null);
  const openingResolveRef = useRef<(() => void) | null>(null);

  const handleCloudClosed = useCallback(() => {
    closingResolveRef.current?.();
    closingResolveRef.current = null;
  }, []);

  const handleCloudOpened = useCallback(() => {
    openingResolveRef.current?.();
    openingResolveRef.current = null;
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isOnboardingRoute = location.pathname.startsWith("/onboarding");
  const disableAssetPreloader = location.pathname === "/";

  // Wrap router.navigate so navigations to/from /world play the pixel
  // cover-then-uncover animation. All other routes pass through to the
  // standard navigate (which fires the slide via defaultViewTransition).
  useEffect(() => {
    const originalNavigate = router.navigate.bind(router);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrappedNavigate = (async (options: any) => {
      const navigateOptions =
        typeof options === "object" && options !== null
          ? options
          : { to: options };

      if (navigateOptions.reloadDocument) {
        return originalNavigate(navigateOptions);
      }

      const currentLocation =
        router.state.location ?? router.state.resolvedLocation;
      const fromPath = currentLocation?.pathname ?? "";

      let toPath: string | undefined;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const built = router.buildLocation(navigateOptions as any);
        toPath = built.pathname;
      } catch {
        if (
          typeof navigateOptions === "object" &&
          navigateOptions !== null &&
          "to" in navigateOptions &&
          typeof navigateOptions.to === "string"
        ) {
          toPath = navigateOptions.to;
        }
      }

      // Pixel cover triggers when:
      //   1. Either side is a cloud route (existing /world behaviour), OR
      //   2. Either side is NOT a bottom-nav tab (deep pages — match/$seed,
      //      /leaderboard, /missions, etc. get pixel; tab-to-tab keeps slide).
      const usePixelCover =
        !!toPath &&
        fromPath !== toPath &&
        document.visibilityState === "visible" &&
        (isCloudRoute(fromPath) ||
          isCloudRoute(toPath) ||
          !isNavbarRoute(fromPath) ||
          !isNavbarRoute(toPath));

      if (!usePixelCover) {
        return originalNavigate(navigateOptions);
      }

      // Suppress the slide view-transition; the pixel mosaic fully covers
      // the screen before the DOM swaps.
      router.shouldViewTransition = false;

      const token = Date.now();
      setPixelTransitionToken(token);

      const closingPromise = new Promise<void>((resolve) => {
        closingResolveRef.current = resolve;
      });
      await closingPromise;

      try {
        if (document.visibilityState === "hidden") {
          router.shouldViewTransition = false;
        }
        const result = await originalNavigate(navigateOptions);
        return result;
      } finally {
        const openingPromise = new Promise<void>((resolve) => {
          openingResolveRef.current = resolve;
        });
        setPixelTransitionToken(null);
        try {
          await openingPromise;
        } finally {
          router.shouldViewTransition = false;
        }
      }
    }) as typeof router.navigate;

    router.navigate = wrappedNavigate;

    return () => {
      router.navigate = originalNavigate;
      closingResolveRef.current?.();
      closingResolveRef.current = null;
      openingResolveRef.current?.();
      openingResolveRef.current = null;
    };
  }, [router]);

  return (
    <>
      {/*{process.env.NODE_ENV === "development" && (
        <Agentation
          endpoint="http://localhost:4747"
          onSessionCreated={(sessionId) => {
            console.log("Session started:", sessionId);
          }}
        />
      )}*/}
      <MaintenanceOverlay />
      <GeoBlockOverlay />
      <HeadContent />
      <Captcha />
      <AssetPreloader disabled={disableAssetPreloader}>
        <MusicProvider>
          <TourProvider>
            {/*<OneSignalInit />*/}
            {/*{isOnboardingRoute ? <OnboardingAuthCheck /> : <AuthCheck />}*/}
            <PosthogLoginIdentifier />
            <MoonPayRecoveryToastNotifier />
            {/*<AuthCheck />*/}
            {MiniKit.isInWorldApp() ? <AuthCheckForWMA /> : <AuthCheck />}
            <Suspense fallback={null}>
              <GlobalReferralCapture />
            </Suspense>
            <Suspense fallback={null}>
              <FirstLoginRedirect />
            </Suspense>
            <Suspense fallback={null}>
              <RefCheck />
            </Suspense>
            <Suspense fallback={null}>
              {allowSnowfall && <SnowfallOverlay style={{ zIndex: 4 }} />}
              <div className="relative flex-1 mx-auto w-full bg-zinc-50 flex flex-col overflow-x-hidden wallpaper-background parallax-background">
                <div className="relative min-h-svh flex flex-1 flex-col mx-auto w-full overflow-x-hidden">
                  {/*{allowPowerUserDialog && <PowerUserDialog />}*/}
                  <WelcomePackDialog />
                  <ReplayTutorialDialog />
                  <Outlet />
                </div>
              </div>
              {/* Pixel cover for /world navigations only. Other routes use
                  the horizontal slide via RouterProvider.tsx. */}
              <PixelPageTransition
                token={pixelTransitionToken}
                tileSize={isDesktop ? 48 : 24}
                // pixelColor={"#FFF0E8"}
                pixelColor={"#000"}
                animationDuration={pixelTransitionDefaults.animationDuration}
                backgroundColor={pixelTransitionDefaults.backgroundColor}
                fadeDuration={pixelTransitionDefaults.fadeDuration}
                onCloseComplete={handleCloudClosed}
                onComplete={handleCloudOpened}
              />
            </Suspense>
            <Toaster
              richColors
              position="top-center"
              className="z-999 pointer-events-auto"
            />
            {!MiniKit.isInWorldApp() && <NotifToastTrigger />}
            <PwaUpdateToast />
            <TanStackRouterDevtools />
            <TeleportWidget />
            <MissionWidget />
            <ChatWidget />
          </TourProvider>
        </MusicProvider>
      </AssetPreloader>
    </>
  );
};

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: PixelErrorFallback,
  notFoundComponent: PixelNotFound,
  beforeLoad: ({ context }) => {
    // Context is provided by the router (from AppRouterProvider)
    // This just passes it through to child routes
    return context;
  },
});
